import React, { useState, useEffect } from "react";
import "./DynamicApplicationCountryTable.css";
import DateInput from "./DateInput";

interface ApplicationCountryEntry {
  id: string;
  country: string;
  status: string;
  other: string;
  fromDate: string;
  toDate: string;
}

interface DynamicApplicationCountryTableProps {
  value: ApplicationCountryEntry[];
  onChange: (entries: ApplicationCountryEntry[]) => void;
  required?: boolean;
}

const DynamicApplicationCountryTable: React.FC<
  DynamicApplicationCountryTableProps
> = ({ value, onChange, required = false }) => {
  // Initialize with single entry if value is empty
  const initializeEntries = () => {
    const safeValue = Array.isArray(value) ? value : [];
    if (safeValue.length === 0) {
      return [
        {
          id: `application_country_${Date.now()}_${Math.random()}`,
          country: "",
          status: "",
          other: "",
          fromDate: "",
          toDate: "",
        },
      ];
    }
    return safeValue;
  };

  const [entries, setEntries] =
    useState<ApplicationCountryEntry[]>(initializeEntries);

  useEffect(() => {
    onChange(entries);
  }, [entries, onChange]);

  const updateEntry = (
    id: string,
    field: keyof ApplicationCountryEntry,
    value: string
  ) => {
    const updatedEntries = entries.map((entry) =>
      entry.id === id ? { ...entry, [field]: value } : entry
    );
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const addEntry = () => {
    const newEntry: ApplicationCountryEntry = {
      id: `application_country_${Date.now()}_${Math.random()}`,
      country: "",
      status: "",
      other: "",
      fromDate: "",
      toDate: "",
    };

    const updatedEntries = [...entries, newEntry];
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (id: string) => {
    const updatedEntries = entries.filter((entry) => entry.id !== id);
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  return (
    <div className="dynamic-application-country-table">
      <div className="table-header">
        <h4>Application Country Details</h4>
        <button
          type="button"
          className="add-btn"
          onClick={addEntry}
          title="Add application country entry"
        >
          + Add Entry
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="empty-state">
          <p>No application country entries yet.</p>
          <button type="button" className="add-btn" onClick={addEntry}>
            + Add Application Country Entry
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entries.map((entry, index) => (
            <div key={entry.id} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(entry.id)}
                  title="Remove this application country entry"
                >
                  ×
                </button>
              </div>

              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>Country</label>
                    <input
                      type="text"
                      value={entry.country}
                      onChange={(e) =>
                        updateEntry(entry.id, "country", e.target.value)
                      }
                      placeholder="e.g., United States"
                    />
                  </div>

                  <div className="field-group">
                    <label>Status</label>
                    <input
                      type="text"
                      value={entry.status}
                      onChange={(e) =>
                        updateEntry(entry.id, "status", e.target.value)
                      }
                      placeholder="e.g., Student, Worker"
                    />
                  </div>

                  <div className="field-group">
                    <label>Other</label>
                    <input
                      type="text"
                      value={entry.other}
                      onChange={(e) =>
                        updateEntry(entry.id, "other", e.target.value)
                      }
                      placeholder="Additional details"
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="field-group">
                    <label>From</label>
                    <input
                      type="date"
                      value={entry.fromDate}
                      onChange={(e) =>
                        updateEntry(entry.id, "fromDate", e.target.value)
                      }
                    />
                  </div>

                  <div className="field-group">
                    <label>To</label>
                    <input
                      type="date"
                      value={entry.toDate}
                      onChange={(e) =>
                        updateEntry(entry.id, "toDate", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {entries.length > 0 && (
        <div className="table-footer">
          <button type="button" className="add-btn" onClick={addEntry}>
            + Add Another Entry
          </button>
        </div>
      )}

      {required && entries.length === 0 && (
        <small className="error-text">
          At least one application country entry is required
        </small>
      )}
    </div>
  );
};

export default DynamicApplicationCountryTable;
